import React, { useEffect, useState } from "react";
import axios from "axios";
import { Play, Tv, X, Clock, User } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * HerzTV — curated video portal for the Herzbewegung.
 * Uses YouTube lite-embed pattern (thumbnail + click-to-play) for fast loading
 * and minimal third-party tracking until the user actually plays a video.
 */
export default function HerzTV() {
  const [data, setData] = useState({ categories: [], items: [] });
  const [activeCat, setActiveCat] = useState("all");
  const [playing, setPlaying] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/herz/videos`)
      .then((r) => setData(r.data || { categories: [], items: [] }))
      .catch(() => setData({ categories: [], items: [] }));
  }, []);

  const filtered = activeCat === "all" ? data.items : data.items.filter((v) => v.category === activeCat);

  return (
    <section className="py-20 border-t border-[#F7EEDD]/5" style={{ background: "#1A0C12" }} data-testid="herz-tv-section">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-10">
          <div className="text-[#D4AF37] text-xs tracking-[0.4em] uppercase mb-3 inline-flex items-center gap-2">
            <Tv size={12} /> Herz-TV
          </div>
          <h2 className="serif text-3xl md:text-5xl font-light leading-tight">
            Geschichten, die das Herz weiten.
          </h2>
          <p className="text-[#F7EEDD]/55 italic mt-4 max-w-2xl mx-auto">
            Handverlesene Videos, die Mitgefühl in Bewegung bringen.
          </p>
        </div>

        {/* Category tabs */}
        {data.categories.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2 mb-10" data-testid="herz-tv-cats">
            <button onClick={() => setActiveCat("all")}
                    className={`px-4 py-2 rounded-full text-xs uppercase tracking-wider transition ${
                      activeCat === "all" ? "bg-[#D4AF37] text-[#1A0C12]" : "border border-[#F7EEDD]/15 text-[#F7EEDD]/65 hover:border-[#D4AF37]/40"
                    }`}
                    data-testid="herz-tv-cat-all">
              Alle
            </button>
            {data.categories.map((c) => (
              <button key={c.key} onClick={() => setActiveCat(c.key)}
                      className={`px-4 py-2 rounded-full text-xs uppercase tracking-wider transition ${
                        activeCat === c.key ? "bg-[#D4AF37] text-[#1A0C12]" : "border border-[#F7EEDD]/15 text-[#F7EEDD]/65 hover:border-[#D4AF37]/40"
                      }`}
                      data-testid={`herz-tv-cat-${c.key}`}>
                {c.label}
              </button>
            ))}
          </div>
        )}

        {/* Video grid */}
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-[#F7EEDD]/45 italic" data-testid="herz-tv-empty">
            Bald gibt es hier Videos zu sehen.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="herz-tv-grid">
            {filtered.map((v) => <VideoCard key={v.id} v={v} onPlay={() => setPlaying(v)} />)}
          </div>
        )}
      </div>

      {/* Player Lightbox */}
      {playing && <PlayerLightbox v={playing} onClose={() => setPlaying(null)} />}
    </section>
  );
}

const VideoCard = ({ v, onPlay }) => {
  const thumb = `https://i.ytimg.com/vi/${v.youtube_id}/hqdefault.jpg`;
  return (
    <button onClick={onPlay}
            className="group text-left rounded-2xl overflow-hidden border border-[#F7EEDD]/10 bg-[#2A1218] hover:border-[#D4AF37]/40 transition shadow-lg"
            data-testid={`herz-tv-card-${v.youtube_id}`}>
      <div className="relative aspect-video overflow-hidden bg-black">
        <img src={thumb} alt={v.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-700"
             onError={(e) => { e.currentTarget.src = `https://i.ytimg.com/vi/${v.youtube_id}/mqdefault.jpg`; }} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-14 h-14 rounded-full bg-[#D4AF37] text-[#1A0C12] flex items-center justify-center shadow-2xl group-hover:scale-110 transition">
            <Play size={22} className="fill-current ml-1" />
          </div>
        </div>
        {v.duration && (
          <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-md bg-black/80 text-[#F7EEDD] text-[10px] flex items-center gap-1">
            <Clock size={9} /> {v.duration}
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="serif text-lg text-[#F7EEDD] leading-tight group-hover:text-[#D4AF37] transition">
          {v.title}
        </div>
        {v.description && (
          <p className="text-xs text-[#F7EEDD]/55 mt-2 leading-relaxed line-clamp-2">{v.description}</p>
        )}
        {v.speaker && (
          <div className="text-[10px] text-[#F7EEDD]/40 mt-3 inline-flex items-center gap-1.5">
            <User size={9} /> {v.speaker}
          </div>
        )}
      </div>
    </button>
  );
};

const PlayerLightbox = ({ v, onClose }) => {
  // Esc to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/85 backdrop-blur-md p-4"
         onClick={onClose} data-testid="herz-tv-player">
      <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose}
                className="absolute -top-12 right-0 w-10 h-10 rounded-full flex items-center justify-center text-[#F7EEDD] hover:bg-white/10 transition"
                data-testid="herz-tv-player-close">
          <X size={20} />
        </button>
        <div className="aspect-video rounded-2xl overflow-hidden border-2 border-[#D4AF37]/30 shadow-2xl bg-black">
          <iframe
            title={v.title}
            src={`https://www.youtube-nocookie.com/embed/${v.youtube_id}?autoplay=1&rel=0&modestbranding=1`}
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
            className="w-full h-full" />
        </div>
        <div className="mt-4 px-2">
          <div className="serif text-2xl text-[#F7EEDD]">{v.title}</div>
          {v.description && <p className="text-sm text-[#F7EEDD]/65 italic mt-2">{v.description}</p>}
        </div>
      </div>
    </div>
  );
};
